import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { X, Send } from 'lucide-react';
import { chatAPI, consultingAPI } from '../../api';
import { parseUTC } from '../../lib/date';

const ChatBox = ({ orderId, order, userRole, onClose, embedded = false, isConsulting = false, user }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollingInterval = useRef(null);

  // Get order ID from props
  const currentOrderId = orderId || order?.order_id;

  useEffect(() => {
    if (currentOrderId) {
      loadMessages();
      
      // Set up polling every 3 seconds for real-time updates
      pollingInterval.current = setInterval(() => {
        loadMessages();
      }, 3000);
    }

    // Cleanup on unmount
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [currentOrderId]);

  const loadMessages = async () => {
    try {
      const data = isConsulting 
        ? await consultingAPI.getMessages(currentOrderId)
        : await chatAPI.getMessages(currentOrderId);
      setMessages(data);
      setError(null);
    } catch (error) {
      console.error('Error loading messages:', error);
      setError('Erreur de chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      if (isConsulting) {
        await consultingAPI.sendMessage(currentOrderId, newMessage);
      } else {
        await chatAPI.sendMessage(currentOrderId, newMessage);
      }
      setNewMessage('');
      // Reload messages immediately after sending
      await loadMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Erreur lors de l\'envoi du message');
    }
  };

  return (
    <Card className={`bg-[#1a1a1a] border-[#A6A6A6]/20 ${embedded ? 'h-full' : 'h-[600px]'} flex flex-col overflow-hidden`}>
      <CardHeader className="border-b border-[#A6A6A6]/20 flex-shrink-0">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-white mb-1">Chat SAV</CardTitle>
            <p className="text-[#A6A6A6] text-sm">Commande {currentOrderId}</p>
          </div>
          {!embedded && onClose && (
            <Button
              onClick={onClose}
              variant="ghost"
              size="icon"
              className="text-[#A6A6A6] hover:text-white hover:bg-[#A6A6A6]/10"
            >
              <X className="h-5 w-5" />
            </Button>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col p-0 min-h-0 overflow-hidden">
        <ScrollArea className="flex-1 min-h-0 p-6">
          {loading ? (
            <div className="text-center py-4">
              <p className="text-[#A6A6A6]">Chargement...</p>
            </div>
          ) : error ? (
            <div className="text-center py-4">
              <p className="text-red-400">{error}</p>
              <Button 
                onClick={loadMessages} 
                variant="outline" 
                className="mt-2 text-sm"
              >
                Réessayer
              </Button>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[#A6A6A6]">Aucun message pour le moment</p>
              <p className="text-[#A6A6A6] text-sm mt-2">Commencez la conversation</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender_role === userRole ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 break-words overflow-wrap-anywhere ${
                      msg.sender_role === userRole
                        ? 'bg-[#C00000] text-white'
                        : 'bg-[#2a2a2a] text-white'
                    }`}
                    style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  >
                    <p className="text-xs opacity-70 mb-1">{msg.sender_name}</p>
                    <p className="whitespace-pre-wrap">{msg.message}</p>
                    <p className="text-xs opacity-70 mt-1">
                      {parseUTC(msg.timestamp).toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <div className="p-4 border-t border-[#A6A6A6]/20 flex-shrink-0">
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Tapez votre message..."
              className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
            />
            <Button type="submit" className="bg-[#C00000] hover:bg-[#C00000]/90">
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChatBox;
